import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_TYPE,
  CustomerData,
  dispatch,
  getAppType,
  getClockInInfo,
  getCustomerInvite,
  getSearchKey,
  getWorkspaceId,
  useAppSelector,
  useLazyGetCustomerFilterQuery,
  useLazyGetCustomerListQuery,
  getAppliedFilters,
  getFilterByRoute,
  FilterRoute,
  AppliedFilters,
  FiltersPayload,
  buildTreeValueMap,
  TreeFilterKeys,
  getNonPeriodicBeatPlanInfo,
  BeatPlanTypeEnum,
  getHasDefaultCfas,
  getHasDefaultDivision,
  removeSearchKey,
  updateExtraDetails,
  getPeriodicBeatPlanInfo,
  updateSendType,
  updateIsSelectedAll,
  clearSelectedItemIds,
  clearSelectedItemsMap,
  getIsSelectedAll,
  useLazyGetCustomerAudienceQuery,
  AudienceCustomer,
  useGetCustomerReportMutation,
  updateSelectedItemIds,
  clearExtraDetails,
} from '@zonofi/zono-money-store';
import { usePaginatedList } from './use-paginated-list';
import { onClickBackToWhatsapp } from '../helper';
import { useNavigate } from 'react-router-dom';
import { FIELD_OPS_ROUTES, SELLER_WEBAPP_ROUTES } from '../constants';
import { inAppModalGroupRef } from '../modals';
import { ChipFilterSection, TripInfoTaskType } from '../types';
import { shallowEqual } from 'react-redux';
import { handleSelectAll } from '../invoice';
import { useResetFlow } from '../customer';
import { useDebounceSearch } from './use-debounce-search';

const buildCustomerListFilterPayload = (
  appliedFilters: AppliedFilters,
  filters: FiltersPayload
) => {
  const payload: Record<string, string[]> = {};
  // ---------- LIST FILTERS ----------
  const { listFilter } = appliedFilters;

  if (listFilter?.CFAs?.length) {
    payload.cfaIds = listFilter.CFAs;
  }

  if (listFilter?.Divisions?.length) {
    payload.divisionIds = listFilter.Divisions;
  }

  // ---------- TREE FILTERS ----------
  const { treeFilter: appliedTreeFilter } = appliedFilters;
  const { treeFilter: availableTreeFilter } = filters;

  if (appliedTreeFilter && availableTreeFilter) {
    Object.entries(appliedTreeFilter).forEach(([treeKey, selectedIds]) => {
      if (!selectedIds?.length) return;

      const tree = availableTreeFilter[treeKey as TreeFilterKeys];
      if (!tree) return;

      const valueMap = buildTreeValueMap(tree.value);
      const resolvedValues = selectedIds
        .map((id) => valueMap[id])
        .filter(Boolean);
      if (!resolvedValues.length) return;

      switch (treeKey) {
        case TreeFilterKeys.CustomerLifeCycle:
          payload.customerLifeCycleGroups = resolvedValues;
          break;
        case TreeFilterKeys.DynamicSegments:
          payload.dynamicSegments = resolvedValues;
          break;
        case TreeFilterKeys.Routes:
          payload.routes = resolvedValues;
          break;
        case TreeFilterKeys.Segments:
          payload.customer_segment = resolvedValues;
          break;
      }
    });
  }
  return payload;
};

const buildCustomerAudienceFilterPayload = (
  appliedFilters: AppliedFilters,
  filters: FiltersPayload
) => {
  const payload: Record<string, string[]> = {};
  // ---------- LIST FILTERS ----------
  const { listFilter } = appliedFilters;
  if (listFilter?.Divisions?.length) {
    payload.divisionIds = listFilter.Divisions;
  }

  // ---------- TREE FILTERS ----------
  const { treeFilter: appliedTreeFilter } = appliedFilters;
  const { treeFilter: availableTreeFilter } = filters;

  if (appliedTreeFilter && availableTreeFilter) {
    Object.entries(appliedTreeFilter).forEach(([treeKey, selectedIds]) => {
      if (!selectedIds?.length) return;

      const tree = availableTreeFilter[treeKey as TreeFilterKeys];
      if (!tree) return;

      const valueMap = buildTreeValueMap(tree.value);
      const resolvedValues = selectedIds
        .map((id) => valueMap[id])
        .filter(Boolean);
      if (!resolvedValues.length) return;

      switch (treeKey) {
        case TreeFilterKeys.Routes:
          payload.routes = resolvedValues;
          break;
        case TreeFilterKeys.Segments:
          payload.customer_segment = resolvedValues;
          break;
      }
    });
  }
  return payload;
};

interface UseCustomerListOptions {
  disableInitialBackReset?: boolean;
  navigateTo?: string;
}

export const useCustomerList = (options?: UseCustomerListOptions) => {
  const navigate = useNavigate();

  const [showSearch, setShowSearch] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('');

  const appType = useAppSelector(getAppType);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, waConfig } = inviteDetails || {};
  const clockInInfo = useAppSelector(getClockInInfo);
  const nonPeriodicBeatPlanInfo = useAppSelector(getNonPeriodicBeatPlanInfo);
  const periodicBeatPlanInfo = useAppSelector(getPeriodicBeatPlanInfo);
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Customer),
    shallowEqual
  );
  const { filters } = useAppSelector(getFilterByRoute(FilterRoute.Customer));
  const hasDefaultCfas = useAppSelector(getHasDefaultCfas);
  const hasDefaultDivisons = useAppSelector(getHasDefaultDivision);
  const isSelectedAll = useAppSelector(getIsSelectedAll);
  const searchKey = useAppSelector(getSearchKey);

  const { handleBack } = useResetFlow(options?.navigateTo);
  const { debouncedSearchValue } = useDebounceSearch();

  const [getCustomerFiltersApi] = useLazyGetCustomerFilterQuery();
  const [getCustomerListApi, customerListApiResponse] =
    useLazyGetCustomerListQuery();
  const [getCustomerAudienceApi, customerAudienceApiResponse] =
    useLazyGetCustomerAudienceQuery();
  const [getCustomerMetricsReportApi] = useGetCustomerReportMutation();
  const [customerMetricsMap, setCustomerMetricsMap] = useState<
    Record<string, { toPay?: number | string; lastInvoicedDate?: string }>
  >({});

  useEffect(() => {
    return () => {
      dispatch(removeSearchKey());
    };
  }, []);

  useEffect(() => {
    if (options?.disableInitialBackReset) return;
    handleBack();
  }, [options?.disableInitialBackReset, handleBack]);

  useEffect(() => {
    getCustomerFiltersApi({ sellerWorkspaceId });
  }, []);

  const fetchCustomersApiCall = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      if (selectedTab) {
        const apiFilters = buildCustomerAudienceFilterPayload(
          appliedFilters,
          filters
        );

        getCustomerAudienceApi({
          sellerWorkspaceId: sellerWorkspaceId,
          excludedAudience: selectedTab === 'exclude',
          ...apiFilters,
          ...params,
        });
      } else {
        const apiFilters = buildCustomerListFilterPayload(
          appliedFilters,
          filters
        );

        getCustomerListApi({
          workspaceId: sellerWorkspaceId,
          includeCFADivision: 1,
          isIncludeSummaryAndTags: true,
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
          ...apiFilters,
          ...params,
        });
      }
    },
    [debouncedSearchValue, appliedFilters, selectedTab, appType]
  );

  const memoizedCustomerData = useMemo(() => {
    return customerListApiResponse?.data?.customers ?? [];
  }, [customerListApiResponse?.data?.customers]);

  const memoizedCustomerAudienceData = useMemo(() => {
    return customerAudienceApiResponse?.data?.customers ?? [];
  }, [customerAudienceApiResponse?.data?.customers]);

  const {
    data: customerDataRaw,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    refresh: refreshCustomerList,
  } = usePaginatedList<CustomerData | AudienceCustomer>({
    fetchDataApiCall: fetchCustomersApiCall,
    apiResponseData: selectedTab
      ? memoizedCustomerAudienceData
      : memoizedCustomerData,
    isLoading: selectedTab
      ? customerAudienceApiResponse.isLoading
      : customerListApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue, appliedFilters, selectedTab],
    isError: selectedTab
      ? customerAudienceApiResponse.isError
      : customerListApiResponse.isError,
    paginationMeta: {
      startRecord: selectedTab
        ? customerAudienceApiResponse?.data?.startRecord || 0
        : customerListApiResponse?.data?.startRecord || 0,
      endRecord: selectedTab
        ? customerAudienceApiResponse?.data?.endRecord || 0
        : customerListApiResponse?.data?.endRecord || 0,
      totalCount: selectedTab
        ? customerAudienceApiResponse?.data?.totalCount || 0
        : customerListApiResponse?.data?.totalCount || 0,
    },
  });

  useEffect(() => {
    if (!sellerWorkspaceId || !customerDataRaw?.length) {
      if (!customerDataRaw?.length) setCustomerMetricsMap({});
      return;
    }

    const customerIds = customerDataRaw
      .map((customer) => customer?.id)
      .filter((id): id is string => Boolean(id));
    if (!customerIds.length) return;

    getCustomerMetricsReportApi({
      params: { sellerWorkspaceId },
      body: {
        customerIds,
        sellerworkspaceid: sellerWorkspaceId,
      },
    })
      .unwrap()
      .then((response) => {
        const nextMap: Record<
          string,
          { toPay?: number | string; lastInvoicedDate?: string }
        > = {};

        (response ?? []).forEach((item) => {
          const customerId = item?.inviteId;
          if (!customerId) return;

          nextMap[customerId] = {
            toPay: item?.outstandingAmount,
            lastInvoicedDate:
              item?.latestInvoiceDate ??
              item?.latestInvoiceDate ??
              item?.latestInvoiceId ??
              item?.latestInvoiceId,
          };
        });

        setCustomerMetricsMap(nextMap);
      })
      .catch(() => {
        setCustomerMetricsMap({});
      });
  }, [sellerWorkspaceId, customerDataRaw, getCustomerMetricsReportApi]);

  const customerData = useMemo(() => {
    return customerDataRaw.map((customer) => {
      const metric = customerMetricsMap[customer?.id];
      if (!metric) return customer;

      return {
        ...customer,
        toPay:
          metric.toPay ??
          customer?.toPay ??
          (customer as any)?.outstandingAmount,
        lastInvoicedDate:
          metric.lastInvoicedDate ??
          customer?.lastInvoicedDate ??
          (customer as any)?.lastInvoicedOn,
      };
    });
  }, [customerDataRaw, customerMetricsMap]);

  const onWhatsAppPress = () => {
    onClickBackToWhatsapp({
      whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      workspaceId: seller?.workspaceId,
    });
  };

  const onAddCustomerPress = () => {
    dispatch(clearExtraDetails());
    if (appType === APP_TYPE.SELLER_HUB) {
      navigate(SELLER_WEBAPP_ROUTES.ADD_CUSTOMER);
      return;
    } else if (appType === APP_TYPE.FIELDS_OPS) {
      if (clockInInfo && clockInInfo?.tripId) {
        navigate(FIELD_OPS_ROUTES.ADD_CUSTOMER);
        return;
      } else {
        inAppModalGroupRef?.current?.openTripInfoModal(
          TripInfoTaskType.NOT_CLOCKED_IN
        );
      }
    }
  };

  const onAddRoutePress = () => {
    const beatType = nonPeriodicBeatPlanInfo?.beatType ?? periodicBeatPlanInfo;
    const customer = customerData?.[0];
    if (!customer) return;

    dispatch(
      updateExtraDetails({
        customerId: customer?.id || '',
        customerName: customer?.name || '',
        companyName: customer?.companyName || '',
        mobile: customer?.mobile || customer?.telephone || '',
      })
    );
    dispatch(removeSearchKey());
    setShowSearch(false);
    if (!hasDefaultCfas || !hasDefaultDivisons) {
      navigate(FIELD_OPS_ROUTES.ADD_CFA);
      return;
    }
    navigate(
      beatType === BeatPlanTypeEnum.Routes
        ? FIELD_OPS_ROUTES.ASSIGN_ROUTES
        : FIELD_OPS_ROUTES.ASSIGN_BEAT_PLAN
    );
  };

  const tabs: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: 'active',
            value: 'Active',
            selected: selectedTab === 'active',
          },
          {
            id: 'exclude',
            value: 'Excluded',
            selected: selectedTab === 'exclude',
          },
        ],
        onItemPress: ({ id }: { id: string }) => {
          if (selectedTab !== id) {
            dispatch(removeSearchKey());
          }
          setSelectedTab(id);
        },
      },
    ],
    [selectedTab]
  );

  const onSendCustomer = () => {
    setSelectedTab('active');
    dispatch(updateSendType('sendManageCustomer'));
  };

  const handleToggleSelectAll = useCallback(() => {
    const nextValue = !isSelectedAll;
    dispatch(updateIsSelectedAll(nextValue));

    if (nextValue) {
      // Selecting all
      handleSelectAll(customerData);
    } else {
      // Unselecting all
      dispatch(clearSelectedItemIds());
      dispatch(clearSelectedItemsMap());
    }
  }, [isSelectedAll, customerData]);

  const onCheckboxToggle = (customer: any) => {
    const customerId = customer?.id || customer?.inviteId;
    if (!customerId) return;
    dispatch(updateSelectedItemIds(customerId));
  };

  return {
    customerData,
    lastItemRef,
    isPageLoading,
    onScroll,
    onWhatsAppPress,
    isloading:
      pageNo === 1 &&
      (customerListApiResponse?.isLoading ||
        customerAudienceApiResponse?.isLoading ||
        customerListApiResponse?.isFetching ||
        customerAudienceApiResponse?.isFetching),
    isEndOfList,
    searchKey,
    onAddCustomerPress,
    showSearch,
    setShowSearch,
    onAddRoutePress,
    tabs,
    selectedTab,
    beatType: nonPeriodicBeatPlanInfo?.beatType ?? periodicBeatPlanInfo,
    handleToggleSelectAll,
    onSendCustomer,
    setSelectedTab,
    fetchCustomersApiCall,
    refreshCustomerList,
    onCheckboxToggle,
  };
};
